import { useCallback, useMemo, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import HeaderItem from "./HeaderItem";
import { TRANSFER_TYPE } from "../../../types/community-transfer.type";
import { setHeaderFilterItems } from "../../../store/reducers/common.slice";

const sortItemsByPriority = (items, priorityData) => {
    if (!priorityData?.length) return items;

    const mainItems = items.filter((item) => item.isMain);
    const nonMainItems = items.filter((item) => !item.isMain);

    const sortedNonMainItems = priorityData.reduce((acc, priorityItem) => {
        const foundItem = nonMainItems.find((item) => {
            if (item.filterType === "ADT") {
                return item.cardId === priorityItem.cardId && item.childId === priorityItem.childId;
            }
            return item.dataKey === priorityItem.type;
        });

        if (foundItem) {
            acc.push(foundItem);
        }
        return acc;
    }, []);

    // Efficiently find remaining items using a Set
    const sortedSet = new Set(sortedNonMainItems);
    const remainingItems = nonMainItems.filter((item) => !sortedSet.has(item));

    return [...mainItems, ...sortedNonMainItems, ...remainingItems];
};

const getTotalNumberOfFilter = (cardFilter, type) => cardFilter?.[type]?.length ?? null;

// Helper function to get selected item names from filter data
const getSelectedItemNames = (cardFilter, dataKey, filterData) => {
    if (!cardFilter?.[dataKey] || !Array.isArray(cardFilter[dataKey]) || cardFilter[dataKey].length === 0) {
        return [];
    }
    
    if (!filterData || !Array.isArray(filterData)) {
        return [];
    }
    
    const selectedIds = cardFilter[dataKey];
    const selectedItems = filterData.filter(item => {
        const itemId = item.id || item._id || item.value;
        return selectedIds.includes(itemId);
    });
    
    return selectedItems.map(item => item.name || item.label || item.title || item.text || 'Unknown').filter(Boolean);
};

// Helper function to get detailed information about selected items
const getSelectedItemDetails = (cardFilter, dataKey, filterData, headerItem) => {
    const names = getSelectedItemNames(cardFilter, dataKey, filterData);
    const count = getTotalNumberOfFilter(cardFilter, dataKey);
    
    // Handle ADT filters differently
    if (headerItem.filterType === "ADT") {
        const adtNames = headerItem.selectedIds?.map(id => {
            const foundItem = headerItem.tableData?.find(item => 
                item.id === id || item._id === id
            );
            return foundItem?.name || foundItem?.label || foundItem?.title || 'Unknown';
        }).filter(Boolean) || [];
        
        return {
            names: adtNames,
            count: adtNames.length,
            displayText: adtNames.length > 0 ? adtNames.join(', ') : '',
            type: 'ADT'
        };
    }
    
    return {
        names,
        count,
        displayText: names.length > 0 ? names.join(', ') : '',
        type: 'standard'
    };
};

const HeaderFilter = ({
    items,
    filterListData,
    cardFilter,
    transferType,
    percentageBy,
    lockedTotal,
    lockedTotalBy,
    lockeByADT,
    openFrom,
    isMobile = false,
    renderCardFilterPopover,
    renderAdtTabFilterPopover,
    interactiveHeaderFilterPopoverSlowTransition = false,
}) => {
    const dispatch = useDispatch();
    const isMountedRef = useRef(true);
    const timeoutRef = useRef(null);
    const lastHashRef = useRef('');

    const shouldShowDefault = useMemo(() => {
        if (openFrom === "detailView") {
            return false;
        }
        const isDefaultCondition =
            !transferType ||
            (Array.isArray(transferType) && transferType.length === 0) ||
            (typeof transferType === 'string' && transferType.trim() === '');

        const hasPriorityData = cardFilter?.priorityData?.length > 0 || cardFilter?.mainPriorityData?.length > 0;

        const isLockedByCensus = lockedTotal && lockedTotalBy === 'census';

        const resBool = (isDefaultCondition && !hasPriorityData) || isLockedByCensus;

        return resBool;
    }, [openFrom, transferType, cardFilter?.priorityData?.length, cardFilter?.mainPriorityData?.length, lockedTotal, lockedTotalBy]);

    const sortedItemsResult = (arrayData, lockedTotalBy, lockeByADT, lockedTotal) => {
        let foundLockedIndex = -1;
        if (!lockedTotal) {
            return arrayData.map((item) => {
                return { ...item, isLocked: false };

            });
        }

        // First pass: Find the index of the matching item
        const updatedItems = arrayData.map((item, index) => {
            if (item?.isCommunity && (lockedTotalBy === TRANSFER_TYPE.AMA || lockedTotalBy === TRANSFER_TYPE.SNF || lockedTotalBy === TRANSFER_TYPE.SAFE_DISCHARGE)) {
                foundLockedIndex = index;
                return { ...item, isLocked: true }; // Lock the matching item
            } else if (item?.filterType === "ADT" && lockeByADT && item.cardId === lockeByADT?.cardId && item.childId === lockeByADT?.childId) {
                foundLockedIndex = index;
                return { ...item, isLocked: true }; // Lock the matching item
            } else if (item.dataKey === lockedTotalBy) {
                foundLockedIndex = index;
                return { ...item, isLocked: true }; // Lock the matching item
            }

            return item;
        });

        // Second pass: Apply isLocked based on the found index
        return updatedItems.map((item, index) => {
            if (foundLockedIndex !== -1 && item.dataKey) {
                if (index < foundLockedIndex) {
                    return { ...item, isLocked: true }; // Lock items before the match
                } else if (index > foundLockedIndex) {
                    return { ...item, isLocked: false }; // Unlock items after the match
                }
            }
            return item;
        });
    };

    const sortedItems = useMemo(() => {
        const arrayData = sortItemsByPriority(items, cardFilter?.priorityData || cardFilter?.mainPriorityData, lockedTotalBy)
        const result = sortedItemsResult(arrayData, lockedTotalBy, lockeByADT, lockedTotal);
        return result
    },
        [cardFilter, items, lockedTotalBy, lockeByADT, lockedTotal]
    );

    const isTransferMatch = useCallback((dataKey) => {
        if (
            ((Array.isArray(transferType) && transferType.includes("all")) || transferType === 'all') &&
            (cardFilter?.priorityData?.length > 0 || cardFilter?.mainPriorityData?.length > 0) &&
            (lockedTotalBy !== 'all' || !lockedTotalBy)
        ) {
            return false;
        }

        if (
            openFrom === "detailView" &&
            (transferType === "all" || (Array.isArray(transferType) && transferType.length === 1 && transferType[0] === "all"))
        ) {
            return false;
        }

        return Array.isArray(transferType)
            ? transferType.includes(dataKey)
            : transferType === dataKey;
    }, [transferType, cardFilter?.priorityData?.length, cardFilter?.mainPriorityData?.length, lockedTotalBy, openFrom]);

    // Debounced dispatch function to prevent infinite loops
    const debouncedDispatch = useCallback((data) => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        
        timeoutRef.current = setTimeout(() => {
            if (isMountedRef.current) {
                dispatch(setHeaderFilterItems(data));
            }
        }, 100); // 100ms debounce
    }, [dispatch]);

    // Save sortedItems to Redux store for report description generation
    useEffect(() => {
        if (!isMountedRef.current) return;

        try {
            const enrichedSortedItems = sortedItems.map(item => ({
                ...item,
                isMain: item.isMain || false,
                filterCount: getTotalNumberOfFilter(cardFilter, item.dataKey),
                filterData: filterListData?.[item.dataKey],
                isActiveFilter: Boolean(cardFilter?.[item.dataKey]?.length > 0),
                isTransferTypeMatch: isTransferMatch(item.dataKey),
                percentageByInfo: percentageBy,
                selectedItemNames: getSelectedItemNames(cardFilter, item.dataKey, filterListData?.[item.dataKey]),
                selectedItemDetails: getSelectedItemDetails(cardFilter, item.dataKey, filterListData?.[item.dataKey], item),
                lockedInfo: {
                    isLocked: item.isLocked,
                    lockedTotalBy,
                    lockedTotal
                }
            }));

            // Create a simple hash for change detection
            const currentHash = JSON.stringify({
                itemsCount: sortedItems.length,
                transferType: Array.isArray(transferType) ? transferType.join(',') : transferType,
                percentageBy,
                lockedTotal,
                lockedTotalBy,
                openFrom,
                activeFiltersCount: enrichedSortedItems.filter(item => item.isActiveFilter).length,
                cardFilterKeysHash: Object.keys(cardFilter || {}).slice().sort().join(','), // Fix: create copy before sorting
                priorityLength: (cardFilter?.priorityData?.length || 0) + (cardFilter?.mainPriorityData?.length || 0)
            });

            // Only dispatch if hash has changed
            if (currentHash !== lastHashRef.current) {
                lastHashRef.current = currentHash;
                
                const newData = {
                    items: enrichedSortedItems,
                    metadata: {
                        transferType,
                        percentageBy,
                        lockedTotal,
                        lockedTotalBy,
                        shouldShowDefault,
                        cardFilterKeys: Object.keys(cardFilter || {}), // Don't mutate, just store
                        priorityDataLength: (cardFilter?.priorityData?.length || 0) + (cardFilter?.mainPriorityData?.length || 0),
                        openFrom,
                        timestamp: Date.now() // Add timestamp for uniqueness
                    }
                };

                debouncedDispatch(newData);
            }
        } catch (error) {
            console.warn('HeaderFilter: Error updating header filter items:', error);
        }
    }, [sortedItems, transferType, percentageBy, lockedTotal, lockedTotalBy, openFrom, cardFilter, shouldShowDefault, isTransferMatch, debouncedDispatch, filterListData]);

    // Cleanup function to prevent memory leaks
    useEffect(() => {
        return () => {
            isMountedRef.current = false;
            lastHashRef.current = '';
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        };
    }, []);

    if(isMobile) {
     return null
    }

    return [
        shouldShowDefault && (
            <HeaderItem
                key="default"
                label={percentageBy}
                type="label"
                isLocked={lockedTotal && lockedTotalBy === 'census'}
            />
        ),
        ...sortedItems.map((item, index) => {
            const {
                label,
                type,
                dataKey,
                isMain = false,
                isLocked,
                filterType,
                subTitle,
                total,
                selectedIds,
                tableData,
            } = item;

            if (isMain && !isTransferMatch(dataKey)) return null;

            const headerItemProps = {
                key: index,
                label: filterType === "ADT" ? `${label}: ${subTitle || ''}`.trim() : label,
                isLocked,
            };

            if (filterType === "ADT") {
                return (
                    <HeaderItem
                        {...headerItemProps}
                        type="filter"
                        total={total}
                        cardFilter={selectedIds}
                        data={tableData}
                        adtHeaderRow={item}
                        renderAdtInteractivePopover={renderAdtTabFilterPopover}
                        interactiveHeaderFilterPopoverSlowTransition={interactiveHeaderFilterPopoverSlowTransition}
                    />
                );
            } else {
                return (
                    <HeaderItem
                        {...headerItemProps}
                        type={type}
                        total={getTotalNumberOfFilter(cardFilter, dataKey)}
                        data={filterListData?.[dataKey]}
                        cardFilter={cardFilter?.[dataKey]}
                        dataKey={dataKey}
                        renderInteractivePopover={renderCardFilterPopover}
                        interactiveHeaderFilterPopoverSlowTransition={interactiveHeaderFilterPopoverSlowTransition}
                    />
                );
            }
        })
    ];
};

export default HeaderFilter;
